import { MAX_WIDTH_CUSTOMIZED_TABLET } from '@components/Hooks/useWidth';
import { CollapsedMode, ContextMode } from '@components/v2/layout/AppTemplate/appTemplate.types';
import { useMediaQuery } from '@mui/material';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useApplicationContext } from '../../../../../modules/context/ApplicationContext/ApplicationContext';
import { SIDEBAR } from '../appTemplate.consts';
import { useSidebarPeek } from './useSidebarPeek';
import { useSidebarState } from './useSidebarState';
import { useTransientState } from './useTransientState';

interface UseAppTemplateParams {
  collapsedMode: CollapsedMode;
  contextMode: ContextMode;
  onBack?: () => void;
}

export const useAppTemplate = ({ collapsedMode, contextMode, onBack }: UseAppTemplateParams) => {
  const isNested = contextMode === 'nested';
  const { pathname } = useLocation();
  const isMobile = useMediaQuery(`(max-width:${MAX_WIDTH_CUSTOMIZED_TABLET}px)`);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { isActive: isBlurring, trigger: triggerBlur } = useTransientState(80);
  const { isActive: isLeavingNested, trigger: triggerLeavingNested } = useTransientState(
    SIDEBAR.transitionDuration,
  );
  const previousNavigation = useRef({ pathname, isNested });
  const previousCollapsedMode = useRef(collapsedMode);

  const { team, brandId } = useApplicationContext();
  const contextKey = `${team?.id ?? ''}:${brandId ?? ''}`;

  const { effectiveSidebarState, toggleSidebar } = useSidebarState({
    collapsedMode,
    isNested,
    contextKey,
  });

  const { peek, peekTrigger, closePeek, undockPeek } = useSidebarPeek({
    isEnabled: !isMobile && effectiveSidebarState === 'hidden' && !isLeavingNested,
    isExpanded: !isMobile && effectiveSidebarState === 'expanded',
  });

  useEffect(() => {
    if (!isMobile) {
      setIsMobileOpen(false);
    }
  }, [isMobile]);

  useLayoutEffect(() => {
    const previous = previousNavigation.current;
    previousNavigation.current = { pathname, isNested };

    if (previous.isNested !== isNested) {
      triggerBlur();

      if (previous.isNested && !isMobile && effectiveSidebarState === 'hidden' && !undockPeek()) {
        triggerLeavingNested();
      }
      return;
    }

    if (previous.pathname !== pathname) {
      setIsMobileOpen(false);
      closePeek();
    }
  }, [
    pathname,
    isNested,
    isMobile,
    effectiveSidebarState,
    triggerBlur,
    triggerLeavingNested,
    closePeek,
    undockPeek,
  ]);

  useEffect(() => {
    if (previousCollapsedMode.current === collapsedMode) {
      return;
    }

    previousCollapsedMode.current = collapsedMode;
    setIsMobileOpen(false);
  }, [collapsedMode]);

  const handleMenuToggle = () => {
    if (isMobile) {
      setIsMobileOpen((prev) => !prev);
      return;
    }

    if (isNested) {
      onBack?.();
      return;
    }

    toggleSidebar();
  };

  const closeMobileSidebar = () => {
    setIsMobileOpen(false);
  };

  return {
    effectiveSidebarState,
    isBlurring,
    handleMenuToggle,
    closeMobileSidebar,
    isMobile,
    isMobileOpen,
    peek,
    peekTrigger,
    sidebarContextMode: isLeavingNested ? 'nested' : contextMode,
  };
};
